import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../context/ProjectContext';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import api from '../../services/api';
import { formatDateTimeDDMMMYYYY, formatOffsetForDisplay } from '../../utils/dateFormat';
import { exportActionItemReportToExcel } from '../../utils/exportToExcel';
import Alert from '../Common/Alert';
import './ReportsHome.css';
import './AuditReport.css';

/** Parse a value as UTC for display. Ensures we show Performed On UTC from DB without applying offset. */
function parseUtcForDisplay(value) {
  if (value == null) return null;
  const s = typeof value === 'string' ? value.trim() : (value instanceof Date ? value.toISOString() : String(value));
  if (!s) return null;
  let asUtc = s;
  if (!asUtc.endsWith('Z') && !asUtc.includes('+')) {
    asUtc = asUtc.replace(' ', 'T') + (asUtc.includes('T') ? '' : 'T00:00:00') + 'Z';
  }
  const d = new Date(asUtc);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** DCF Report Excel column order: Sr No., Project, Site No, Visit Type, VisitNo, Field Name/Attribute, Original Value, Corrected Value, Status, Performed By, Performed On UTC, Performed At Offset. */
const DCF_EXPORT_COLUMNS = [
  { key: 'srNo', label: 'Sr No.' },
  { key: 'project', label: 'Project' },
  { key: 'siteNo', label: 'Site' },
  { key: 'visitType', label: 'Visit Type' },
  { key: 'visitNo', label: 'Visit Id' },
  { key: 'fieldName', label: 'Field Name' },
  { key: 'originalValue', label: 'Previous Value' },
  { key: 'correctedValue', label: 'Current Value' },
  { key: 'status', label: 'Status' },
  { key: 'performedBy', label: 'Performed By' },
  { key: 'performedOnUTC', label: 'Performed On UTC' },
  { key: 'performedAtOffset', label: 'Performed At Offset' },
];

/** Column widths for DCF Report (Sr No., Project, Site No, Visit Type, VisitNo, Field Name, Original/Corrected Value, Status, Performed By, Performed on UTC, Performed At Offset). */
const DCF_COL_WIDTHS = [7, 18, 28, 22, 14, 28, 32, 32, 14, 36, 20, 18];

const DCFReport = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { vRecordedSign, vRecordedAtOffset } = useSession();
  const { currentProject: contextProject } = useProject();
  const projectCode = contextProject && contextProject !== 'all' ? (typeof contextProject === 'string' ? contextProject : contextProject?.projectCode ?? contextProject?.ProjectCode) : '';

  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleExportExcel = async () => {
    setError(null);
    setExportLoading(true);
    try {
      const params = {};
      if (projectCode) params.projectCode = projectCode;
      params.limit = 10000;
      const res = await api.get('/dcf/export', { params });
      const list = Array.isArray(res.data) ? res.data : [];
      const rows = list.map((d, idx) => {
        const utcRaw = d.PerformedOnUTC ?? d.performedOnUTC;
        const utcDate = parseUtcForDisplay(utcRaw);
        const performedOnUtcDisplay = utcDate != null ? formatDateTimeDDMMMYYYY(utcDate, true) : '';
        const offsetRaw = d.PerformedAtOffset ?? d.performedAtOffset ?? '';
        const performedAtOffsetDisplay = offsetRaw !== '' && formatOffsetForDisplay(offsetRaw) !== '—' ? formatOffsetForDisplay(offsetRaw) : offsetRaw || '';
        const projectDisplay = (d.ProjectCode ?? d.projectCode ?? '').toString().trim() || (d.ProjectNo ?? d.projectNo ?? '').toString();
        const siteDisplay = (d.SiteDisplay ?? d.siteDisplay ?? '').toString().trim()
          || (() => {
            const code = (d.SiteCode ?? d.siteCode ?? '').toString().trim();
            const desc = (d.SiteDescription ?? d.siteDescription ?? '').toString().trim();
            return desc ? `${code} - ${desc}` : (code || (d.SiteNo ?? d.siteNo ?? '').toString());
          })();
        const visitTypeDisplay = (d.VisitTypeDisplay ?? d.visitTypeDisplay ?? '').toString().trim()
          || (d.VisitType ?? d.visitType ?? '').toString();
        return {
          srNo: idx + 1,
          project: projectDisplay,
          siteNo: siteDisplay,
          visitType: visitTypeDisplay,
          visitNo: d.VisitNo ?? d.visitNo ?? '',
          fieldName: d.FieldName ?? d.fieldName ?? '',
          originalValue: d.OriginalValue ?? d.originalValue ?? '',
          correctedValue: d.CorrectedValue ?? d.correctedValue ?? '',
          status: d.Status ?? d.status ?? '',
          performedBy: d.PerformedBy ?? d.performedBy ?? '',
          performedOnUTC: performedOnUtcDisplay,
          performedAtOffset: performedAtOffsetDisplay,
        };
      });
      const fileName = `DCF_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      // Performed by: "Full Name (Username)(Role)" e.g. Kamil Prajapati (sspl14bb)(CRA)
      const firstName = (user?.firstName ?? user?.FirstName ?? user?.vFirstName ?? '').trim();
      const lastName = (user?.lastName ?? user?.LastName ?? user?.vLastName ?? '').trim();
      const userName = (user?.username ?? user?.Username ?? user?.vLoginName ?? user?.vUserName ?? '').trim();
      const profile = (user?.role ?? user?.Role ?? user?.profileCode ?? user?.vUserTypeCode ?? '').trim();
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      const exportedBy = fullName ? `${fullName} (${userName || '—'})(${profile || '—'})` : (vRecordedSign && vRecordedSign.trim() !== '' ? vRecordedSign.trim() : (userName ? `(${userName})(${profile || '—'})` : ''));
      // Performed on UTC: DD-MMM-YYYY HH:mm e.g. 18-Mar-2026 04:26
      const exportedOnUtc = formatDateTimeDDMMMYYYY(new Date(), true);
      // Performed At Offset: same as Audit Report — use session when available
      const offset = (vRecordedAtOffset && String(vRecordedAtOffset).trim() !== '') ? String(vRecordedAtOffset).trim() : '+5:30';
      exportActionItemReportToExcel(rows, DCF_EXPORT_COLUMNS, fileName, {
        formName: 'DCF Report',
        exportedBy,
        exportedOnUtc,
        offset,
        sheetName: 'DCF Report',
        readOnlyAllowResize: true,
        colWidths: DCF_COL_WIDTHS,
      });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Export failed.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="audit-report">
      <div className="audit-report-header" style={{ marginBottom: '16px' }}>
        <h2>DCF Report</h2>
      </div>

      {error && <Alert type="error" message={error} />}

     

      <div className="audit-report-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleExportExcel}
          disabled={exportLoading}
          title={exportLoading ? 'Exporting…' : 'Export to Excel'}
        >
          {exportLoading ? 'Exporting…' : 'Export'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/reports')}>
          Close
        </button>
      </div>
    </div>
  );
};

export default DCFReport;
